/**
 * FAQ Component
 *
 * Accessible accordion-style FAQ section for blog posts.
 * Renders with proper semantic structure for SEO and screen readers.
 */

"use client";

import * as React from "react";
import { flushSync } from "react-dom";
import { cn } from "@/lib/utils";
import { TYPOGRAPHY, SPACING_SCALE, SCROLL_OFFSET } from '@/lib/design-tokens';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface FAQItem {
  question: React.ReactNode;
  answer: React.ReactNode;
  /** Anchor id for the item (the source heading's id in MDX usage) */
  id?: string;
}

export interface FAQProps {
  /** Array of FAQ items with questions and answers */
  items?: FAQItem[];
  /** Optional title for the FAQ section */
  title?: string;
  /** Additional className for styling */
  className?: string;
  /** Children to render as FAQ items (for MDX usage) */
  children?: React.ReactNode;
}

/**
 * Individual FAQ Question component for MDX usage
 */
export function FAQQuestion({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("faq-question", className)} data-faq-question>
      {children}
    </span>
  );
}

/**
 * Individual FAQ Answer component for MDX usage
 */
export function FAQAnswer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("faq-answer", className)} data-faq-answer>
      {children}
    </div>
  );
}

/**
 * Item values to open for an in-page target id: the item whose own id
 * matches, or the item whose (possibly unmounted) answer contains it.
 */
function buildTargetMap(items: FAQItem[]): Map<string, string> {
  const targets = new Map<string, string>();
  items.forEach((item, index) => {
    const value = `item-${index}`;
    if (item.id) targets.set(item.id, value);
    for (const id of collectIds(item.answer)) {
      if (!targets.has(id)) targets.set(id, value);
    }
  });
  return targets;
}

/** Collect every string `id` prop in a React node tree */
function collectIds(node: React.ReactNode, ids: string[] = []): string[] {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as { id?: unknown; children?: React.ReactNode };
    if (typeof props.id === "string") ids.push(props.id);
    collectIds(props.children, ids);
  });
  return ids;
}

/** Decode the target id from a `#fragment` (hash or href) */
function idFromFragment(fragment: string | null | undefined): string | null {
  if (!fragment || !fragment.startsWith("#") || fragment.length < 2) return null;
  try {
    return decodeURIComponent(fragment.slice(1));
  } catch {
    return fragment.slice(1);
  }
}

/**
 * Accordion that keeps every item's anchor id in the server HTML and opens
 * the matching item when the URL hash or an in-page link (TOC, autolink)
 * targets it. `type="multiple"` so opening a target never collapses an item
 * above it, which would shift the scroll position mid-navigation.
 */
function FAQAccordion({
  items,
  triggerClassName,
  contentClassName,
}: {
  items: FAQItem[];
  triggerClassName: string;
  contentClassName: string;
}) {
  const [openItems, setOpenItems] = React.useState<string[]>(["item-0"]);
  const targets = React.useMemo(() => buildTargetMap(items), [items]);

  React.useEffect(() => {
    if (targets.size === 0) return;

    const open = (value: string, sync: boolean) => {
      const update = () =>
        setOpenItems((current) =>
          current.includes(value) ? current : [...current, value]
        );
      // A link click must mount the target before the link's own handler
      // looks it up, so commit synchronously there.
      if (sync) flushSync(update);
      else update();
    };

    const openForHash = () => {
      const id = idFromFragment(window.location.hash);
      const value = id ? targets.get(id) : undefined;
      if (value) open(value, false);
    };

    // Capture phase: runs before TOC handlers that preventDefault and scroll
    // without ever changing location.hash.
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>('a[href^="#"]');
      const id = idFromFragment(link?.getAttribute("href"));
      const value = id ? targets.get(id) : undefined;
      if (value) open(value, true);
    };

    openForHash();
    window.addEventListener("hashchange", openForHash);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("hashchange", openForHash);
      document.removeEventListener("click", onClick, true);
    };
  }, [targets]);

  return (
    <Accordion
      type="multiple"
      value={openItems}
      onValueChange={setOpenItems}
      className="w-full"
    >
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          id={item.id}
          className={cn("border-none", item.id && SCROLL_OFFSET.heading)}
        >
          <div className="relative">
            <AccordionTrigger className={triggerClassName}>
              {item.question}
            </AccordionTrigger>
            {/* Bottom border with gradient fade effect */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-border via-border to-transparent" />
          </div>
          <AccordionContent className={contentClassName}>
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

/**
 * FAQ Section component
 *
 * Can be used in two ways:
 * 1. With items prop: <FAQ items={[{ question: "...", answer: "..." }]} />
 * 2. With children: Uses h3/h4 headers as questions and following content as answers
 */
export function FAQ({ items, title, className, children }: FAQProps) {
  // If items are provided directly, use them
  if (items && items.length > 0) {
    return (
      <section
        className={cn("my-8", className)}
        aria-labelledby={title ? "faq-title" : undefined}
      >
        {title && (
          <h2
            id="faq-title"
            className={cn(TYPOGRAPHY.accordion.heading, `mb-${SPACING_SCALE.md}`)}
            style={{ fontFamily: "var(--font-sans)" }}
          >
            {title}
          </h2>
        )}
        <FAQAccordion
          items={items}
          triggerClassName={TYPOGRAPHY.accordion.trigger}
          contentClassName="text-muted-foreground"
        />
      </section>
    );
  }

  // If children are provided, parse them to extract Q&A pairs
  // This handles MDX content where FAQ items are defined inline
  if (children) {
    const faqItems = parseFAQChildren(children);

    if (faqItems.length > 0) {
      return (
        <section
          className={cn("my-8", className)}
          aria-labelledby={title ? "faq-title" : undefined}
        >
          {title && (
            <h2
              id="faq-title"
              className={cn(TYPOGRAPHY.accordion.heading, "mb-4")}
              style={{ fontFamily: "var(--font-sans)" }}
            >
              {title}
            </h2>
          )}
          <FAQAccordion
            items={faqItems}
            triggerClassName="text-left font-medium"
            contentClassName="prose prose-sm dark:prose-invert max-w-none"
          />
        </section>
      );
    }

    // Fallback: just render children as-is
    return (
      <section className={cn("my-8", className)}>
        {title && (
          <h2
            id="faq-title"
            className={cn(TYPOGRAPHY.accordion.heading, `mb-${SPACING_SCALE.md}`)}
            style={{ fontFamily: "var(--font-sans)" }}
          >
            {title}
          </h2>
        )}
        {children}
      </section>
    );
  }

  return null;
}

/**
 * A heading's content minus the self-link rehype-autolink-headings wraps it
 * in: the question renders inside the trigger <button>, which must not
 * contain another interactive element.
 */
function unwrapSelfLink(content: React.ReactNode, id: string | undefined): React.ReactNode {
  if (!id || !React.isValidElement(content)) return content;
  const props = content.props as { href?: unknown; children?: React.ReactNode };
  return props.href === `#${id}` ? props.children : content;
}

/**
 * Parse children to extract FAQ question/answer pairs
 * Looks for h3/h4 elements as questions and following content as answers.
 * The heading element itself is not rendered, so its id is carried onto the
 * item to keep TOC and share links resolvable.
 */
function parseFAQChildren(children: React.ReactNode): FAQItem[] {
  const items: FAQItem[] = [];
  const childArray = React.Children.toArray(children);

  let currentQuestion: React.ReactNode | null = null;
  let currentId: string | undefined;
  let currentAnswer: React.ReactNode[] = [];

  const flushCurrentItem = () => {
    if (currentQuestion && currentAnswer.length > 0) {
      items.push({
        question: currentQuestion,
        answer:
          currentAnswer.length === 1 ? currentAnswer[0] : <>{currentAnswer}</>,
        id: currentId,
      });
    }
    currentQuestion = null;
    currentId = undefined;
    currentAnswer = [];
  };

  for (const child of childArray) {
    if (!React.isValidElement(child)) {
      if (currentQuestion) {
        currentAnswer.push(child);
      }
      continue;
    }

    const type = child.type;
    const typeName =
      typeof type === "string"
        ? type
        : typeof type === "function"
          ? type.name
          : "";

    // Check if this is a heading (h3, h4) - treat as question
    if (
      typeName === "h3" ||
      typeName === "h4" ||
      type === "h3" ||
      type === "h4"
    ) {
      flushCurrentItem();
      const props = child.props as { id?: unknown; children?: React.ReactNode };
      currentId = typeof props.id === "string" ? props.id : undefined;
      currentQuestion = unwrapSelfLink(props.children, currentId);
    } else if (currentQuestion) {
      // This is answer content
      currentAnswer.push(child);
    }
  }

  // Don't forget the last item
  flushCurrentItem();

  return items;
}

export default FAQ;
